import {
  type ScanRecord,
  getValidSets,
  getInvalidSets,
} from "./StorageService";

// Types for report parameters
export interface DateRangeParams {
  startDate: string;
  endDate: string;
}

export interface ExactDateParams {
  date: string;
}

export interface SummaryReportParams {
  type: "daily" | "weekly" | "monthly";
  date?: string; // Optional specific date
}

// Type for summary report data
export interface SummaryData {
  period: string;
  totalScans: number;
  passedScans: number;
  failedScans: number;
  manualOverrideCount: number; // Added manual override count
  operators: string[];
}

class ReportService {
  // Helper to convert slotId to readable camera name
  private getCameraName(slotId: number): string {
    const cameraNames = ["Camera 1", "Camera 2", "Camera 3"];
    return cameraNames[slotId] || `Camera ${slotId + 1}`;
  }

  // Helper to convert records object to array
  private getAllScanRecords(
    scanHistory: Record<string, ScanRecord[]>
  ): ScanRecord[] {
    return Object.values(scanHistory).flat();
  }

  // Generate CSV content for detailed scan reports (updated headers)
  private generateDetailedCSV(records: ScanRecord[]): string {
    // Updated CSV header - removed Result, added Camera and Session ID, changed Operator to Verificator
    let csv = "Verificator,Camera,Session ID,Timestamp,Code,Notes\n";

    // Add each record
    records.forEach((record) => {
      const timestamp = new Date(record.timestamp).toLocaleString();
      const camera = this.getCameraName(record.slotId);
      const sessionId = record.sessionId || "";
      // Escape any commas in the code
      const safeCode = record.code.includes(",")
        ? `"${record.code}"`
        : record.code;
      const notes = record.isManualOverride ? "Recording Mode: Manual" : "";

      csv += `${record.operatorName},${camera},${sessionId},${timestamp.replace(
        ",",
        " -"
      )},${safeCode},"${notes}"\n`;
    });

    return csv;
  }

  // Generate CSV content for summary reports (updated headers)
  private generateSummaryCSV(summaries: SummaryData[]): string {
    // Updated CSV header - changed Operator to Verificator
    let csv =
      "Period,Total Scans,Passed Scans,Failed Scans,Manual Recording Count,Verificators\n";

    // Add each summary
    summaries.forEach((summary) => {
      const verificators = summary.operators.join("; ");

      csv += `${summary.period},${summary.totalScans},${summary.passedScans},${summary.failedScans},${summary.manualOverrideCount},"${verificators}"\n`;
    });

    return csv;
  }

  // Create a downloadable CSV file
  private downloadCSV(csvContent: string, fileName: string): void {
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  // Generate all scans report
  generateAllScansReport(scanHistory: Record<string, ScanRecord[]>): void {
    const allRecords = this.getAllScanRecords(scanHistory);
    const csv = this.generateDetailedCSV(allRecords);
    const fileName = `all_scans_report_${
      new Date().toISOString().split("T")[0]
    }.csv`;
    this.downloadCSV(csv, fileName);
  }

  // Generate report for an exact date
  generateExactDateReport(
    scanHistory: Record<string, ScanRecord[]>,
    params: ExactDateParams
  ): void {
    const records = scanHistory[params.date] || [];
    const csv = this.generateDetailedCSV(records);
    const fileName = `scan_report_${params.date}.csv`;
    this.downloadCSV(csv, fileName);
  }

  // Generate report for a date range
  generateDateRangeReport(
    scanHistory: Record<string, ScanRecord[]>,
    params: DateRangeParams
  ): void {
    const allDates = Object.keys(scanHistory).sort();
    const filteredDates = allDates.filter(
      (date) => date >= params.startDate && date <= params.endDate
    );

    const records = filteredDates.flatMap((date) => scanHistory[date] || []);
    const csv = this.generateDetailedCSV(records);
    const fileName = `scan_report_${params.startDate}_to_${params.endDate}.csv`;
    this.downloadCSV(csv, fileName);
  }

  // NEW: Generate Valid Sets Report
  generateValidSetsReport(): void {
    const validSetsData = getValidSets();
    let csv =
      "Session ID,Verificator,Timestamp,Camera 1 Code,Camera 2 Code,Camera 3 Code,Result\n";

    Object.values(validSetsData)
      .flat()
      .forEach((set) => {
        const timestamp = new Date(set.timestamp).toLocaleString();
        const [code1, code2, code3] = set.codes;

        csv += `${set.sessionId},${set.operatorName},${timestamp.replace(
          ",",
          " -"
        )},${code1},${code2},${code3},ALL MATCH\n`;
      });

    const fileName = `valid_sets_report_${
      new Date().toISOString().split("T")[0]
    }.csv`;
    this.downloadCSV(csv, fileName);
  }

  // NEW: Generate Invalid Sets Report
  generateInvalidSetsReport(): void {
    const invalidSetsData = getInvalidSets();
    let csv =
      "Session ID,Verificator,Timestamp,Camera 1 Code,Camera 2 Code,Camera 3 Code,Mismatches\n";

    Object.values(invalidSetsData)
      .flat()
      .forEach((set) => {
        const timestamp = new Date(set.timestamp).toLocaleString();
        const [code1, code2, code3] = set.codes;

        // Build mismatch details string
        const mismatches: string[] = [];
        if (set.mismatchDetails.slot1VsSlot2)
          mismatches.push("Camera 1 ≠ Camera 2");
        if (set.mismatchDetails.slot1VsSlot3)
          mismatches.push("Camera 1 ≠ Camera 3");
        if (set.mismatchDetails.slot2VsSlot3)
          mismatches.push("Camera 2 ≠ Camera 3");

        const mismatchString = mismatches.join("; ");

        csv += `${set.sessionId},${set.operatorName},${timestamp.replace(
          ",",
          " -"
        )},${code1},${code2},${code3},"${mismatchString}"\n`;
      });

    const fileName = `invalid_sets_report_${
      new Date().toISOString().split("T")[0]
    }.csv`;
    this.downloadCSV(csv, fileName);
  }

  // NEW: Generate Camera Performance Report
  generateCameraSpecificReport(
    scanHistory: Record<string, ScanRecord[]>,
    cameraSlotId: number
  ): void {
    const allRecords = this.getAllScanRecords(scanHistory);
    const cameraRecords = allRecords.filter(
      (record) => record.slotId === cameraSlotId
    );

    const csv = this.generateDetailedCSV(cameraRecords);
    const cameraName = this.getCameraName(cameraSlotId);
    const fileName = `${cameraName
      .toLowerCase()
      .replace(" ", "_")}_performance_report_${
      new Date().toISOString().split("T")[0]
    }.csv`;
    this.downloadCSV(csv, fileName);
  }

  // Update existing methods to use new terminology
  generateDailySummary(
    scanHistory: Record<string, ScanRecord[]>,
    params?: SummaryReportParams
  ): void {
    const summaries: SummaryData[] = [];
    const dates = params?.date
      ? [params.date]
      : Object.keys(scanHistory).sort();

    dates.forEach((date) => {
      const records = scanHistory[date] || [];
      if (records.length === 0) return;

      const passedScans = records.filter((r) => r.passed).length;
      const failedScans = records.length - passedScans;
      const manualOverrideCount = records.filter(
        (r) => r.isManualOverride
      ).length;

      // Get unique verificators (changed from operators)
      const operators = [...new Set(records.map((r) => r.operatorName))];

      summaries.push({
        period: new Date(date).toLocaleDateString(),
        totalScans: records.length,
        passedScans,
        failedScans,
        manualOverrideCount,
        operators,
      });
    });

    const csv = this.generateSummaryCSV(summaries);
    const fileName = `daily_summary_${
      new Date().toISOString().split("T")[0]
    }.csv`;
    this.downloadCSV(csv, fileName);
  }

  // Group records by week
  private getWeeklyData(
    scanHistory: Record<string, ScanRecord[]>
  ): Record<string, ScanRecord[]> {
    const weeklyData: Record<string, ScanRecord[]> = {};

    Object.entries(scanHistory).forEach(([date, records]) => {
      // Get the week number and year
      const d = new Date(date);
      const firstDayOfYear = new Date(d.getFullYear(), 0, 1);
      const pastDaysOfYear =
        (d.getTime() - firstDayOfYear.getTime()) / 86400000;
      const weekNum = Math.ceil(
        (pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7
      );
      const weekKey = `${d.getFullYear()}-W${weekNum}`;

      if (!weeklyData[weekKey]) {
        weeklyData[weekKey] = [];
      }

      weeklyData[weekKey].push(...records);
    });

    return weeklyData;
  }

  // Generate weekly summary
  generateWeeklySummary(scanHistory: Record<string, ScanRecord[]>): void {
    const weeklyData = this.getWeeklyData(scanHistory);
    const summaries: SummaryData[] = [];

    Object.entries(weeklyData).forEach(([weekKey, records]) => {
      const passedScans = records.filter((r) => r.passed).length;
      const failedScans = records.length - passedScans;
      const manualOverrideCount = records.filter(
        (r) => r.isManualOverride
      ).length;

      // Get unique operators
      const operators = [...new Set(records.map((r) => r.operatorName))];

      summaries.push({
        period: weekKey,
        totalScans: records.length,
        passedScans,
        failedScans,
        manualOverrideCount,
        operators,
      });
    });

    const csv = this.generateSummaryCSV(summaries);
    const fileName = `weekly_summary_${
      new Date().toISOString().split("T")[0]
    }.csv`;
    this.downloadCSV(csv, fileName);
  }

  // Group records by month
  private getMonthlyData(
    scanHistory: Record<string, ScanRecord[]>
  ): Record<string, ScanRecord[]> {
    const monthlyData: Record<string, ScanRecord[]> = {};

    Object.entries(scanHistory).forEach(([date, records]) => {
      // Get the month and year
      const d = new Date(date);
      const monthKey = `${d.getFullYear()}-${d.getMonth() + 1}`;

      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = [];
      }

      monthlyData[monthKey].push(...records);
    });

    return monthlyData;
  }

  // Generate monthly summary
  generateMonthlySummary(scanHistory: Record<string, ScanRecord[]>): void {
    const monthlyData = this.getMonthlyData(scanHistory);
    const summaries: SummaryData[] = [];

    Object.entries(monthlyData).forEach(([monthKey, records]) => {
      const [year, month] = monthKey.split("-");
      const monthName = new Date(
        parseInt(year),
        parseInt(month) - 1,
        1
      ).toLocaleString("default", { month: "long" });

      const passedScans = records.filter((r) => r.passed).length;
      const failedScans = records.length - passedScans;
      const manualOverrideCount = records.filter(
        (r) => r.isManualOverride
      ).length;

      // Get unique operators
      const operators = [...new Set(records.map((r) => r.operatorName))];

      summaries.push({
        period: `${monthName} ${year}`,
        totalScans: records.length,
        passedScans,
        failedScans,
        manualOverrideCount,
        operators,
      });
    });

    const csv = this.generateSummaryCSV(summaries);
    const fileName = `monthly_summary_${
      new Date().toISOString().split("T")[0]
    }.csv`;
    this.downloadCSV(csv, fileName);
  }
}

export default new ReportService();
